/* eslint-disable */
import * as React from "react";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core";
import { ITooltipParams } from "ag-grid-community";
import AgGridTableTooltipOnWrappedCell from "../AgGridTableTooltipOnWrappedCell";
import { TOOLTIP_ANIMATION_CLASS, ANIMATION_START_TIMEOUT, ROW_ID_KEY } from "../../../constants";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: theme.palette.common.white,
            padding: theme.spacing(1, 0.5),
            borderRadius: 3,
            fontSize: 12,
            border: "1px solid #c0c0c0",
            boxShadow: "2px 2px 3px -1px #c0c0c0",
            userSelect: "none",
            cursor: "default",
            "& > p": {
                padding: theme.spacing(0.25),
                margin: 0
            },
        },
    });

export interface AgGridTableBaseTooltipState {
    lastState?: {
        top: number;
        left: number;
        element: HTMLDivElement;
    },
    tooltipRanderedAction?: (tooltipElement: HTMLDivElement) => void;
    tooltipUnmountAction?: (tooltipElement: HTMLDivElement) => void;
}

export interface AgGridTableBaseTooltipParametars {
    selectionAxisColumnId: string;
    getState: () => AgGridTableBaseTooltipState;
    agGridReact?: {
        eGridDiv: HTMLDivElement;
    };
}

export interface AgGridTableBaseTooltipProps
    extends ITooltipParams, AgGridTableBaseTooltipParametars {
}

type Props = AgGridTableBaseTooltipProps & WithStyles<typeof styles>;

class AgGridTableBaseTooltip extends React.Component<Props> {
    private _timeoutId?: number;

    render() {
        const { api, rowIndex, colDef, classes, getState, selectionAxisColumnId, children } = this.props;
        const state = getState();
        state.tooltipRanderedAction = this._tooltipRenderedAction;
        state.tooltipUnmountAction = this._tooltipUnmountAction;

        if (rowIndex == null) {
            return (
                <AgGridTableTooltipOnWrappedCell
                    className={classes.root}
                    tableNode={this.props.agGridReact!.eGridDiv}
                    query={`.ag-header .ag-header-row[aria-rowindex="1"] .ag-header-cell[col-id="${
                        colDef.field}"] .ag-header-cell-text`}
                />
            );
        }
        const { field } = colDef;
        if (!children && field === selectionAxisColumnId) {
            const row = api.getDisplayedRowAtIndex(rowIndex);
            const rowId = row[ROW_ID_KEY];
            return (
                <AgGridTableTooltipOnWrappedCell
                    className={classes.root}
                    tableNode={this.props.agGridReact!.eGridDiv}
                    query={`.ag-body-viewport .ag-row[row-id="${rowId}"] > .ag-cell[col-id="${field}"]`}
                />
            );
        }
        return children ? (
            <div className={classes.root}> 
                {children}
            </div>
        ): null;
    }

    componentWillUnmount() {
        const state = this.props.getState();
        delete state.tooltipRanderedAction;
    }

    private _tooltipRenderedAction = (tooltipElement: HTMLDivElement) => {
        if (this._timeoutId) {
            window.clearTimeout(this._timeoutId);
        }
        tooltipElement.classList.remove(TOOLTIP_ANIMATION_CLASS);
        const state = this.props.getState();
        const { lastState } = state;
        const { style } = tooltipElement;
        const top = parseInt(style.top!);
        const left = parseInt(style.left!);

        style.top = "0";
        style.left = "0";
        if (lastState) {
            style.transform = this._getTransform(lastState.left, lastState.top);
            window.setTimeout(() => {
                tooltipElement.classList.add(TOOLTIP_ANIMATION_CLASS);
                style.transform = this._getTransform(left, top);
            }, ANIMATION_START_TIMEOUT);
        }
        else {
            style.transform = this._getTransform(left, top);
        }
        state.lastState = {
            top,
            left,
            element: tooltipElement,
        };
    }

    private _getTransform = (x: number, y: number) => (
        `translate(${x}px,${y}px)`
    )

    private _tooltipUnmountAction = () => {
        this._timeoutId = window.setTimeout(this._handleTooltipUnmountTimeout, ANIMATION_START_TIMEOUT);
    }

    private _handleTooltipUnmountTimeout = () => {
        const state = this.props.getState();
        delete state.tooltipUnmountAction;
        delete state.lastState;
    }
}

export default withStyles(styles)(AgGridTableBaseTooltip);